import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CtaButton } from '@components/button-cta.component';
import { PreviewOptimise } from '@models/preview-optimise.model';
import * as L from 'leaflet';

@Component({
  selector: 'modal-optimise-preview',
  templateUrl: './modal-optimise-preview.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe, CtaButton],
})
export class ModalOptimisePreviewComponent implements AfterViewInit, OnDestroy {
  preview = input.required<PreviewOptimise>();

  protected onCancel = output<void>();
  protected onConfirm = output<void>();

  private readonly mapContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');

  #map: L.Map | null = null;

  // Palette de couleurs pour distinguer les tournées sur la carte
  readonly #routeColors = ['#2563eb', '#e11d48', '#16a34a', '#d97706'];

  ngAfterViewInit(): void {
    this.#initMap();
  }

  ngOnDestroy(): void {
    this.#map?.remove();
    this.#map = null;
  }

  /** Formate une distance en mètres vers des kilomètres */
  protected formatDistance(meters: number): string {
    return `${(meters / 1000).toFixed(1)} km`;
  }

  /** Formate une durée en secondes vers des minutes */
  protected formatDuration(seconds: number): string {
    return `${Math.round(seconds / 60)} min`;
  }

  #initMap(): void {
    const preview = this.preview();

    const map = L.map(this.mapContainer().nativeElement, {
      center: preview.depot,
      zoom: 14,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    const bounds = L.latLngBounds([]);

    // Marqueur du dépôt (départ et arrivée)
    L.marker(preview.depot, { icon: this.#depotIcon() }).addTo(map);
    bounds.extend(preview.depot);

    preview.routes.forEach((route, index) => {
      const color = this.#routeColors[index % this.#routeColors.length];

      // Tracé de l'itinéraire
      L.polyline(route.polyline, {
        color,
        weight: 4,
        opacity: 0.8,
      }).addTo(map);

      // Marqueurs des foyers (points de passage)
      route.waypoints
        .filter(waypoint => waypoint.type === 'job')
        .forEach(waypoint => {
          L.marker([waypoint.lat, waypoint.lng], {
            icon: this.#jobIcon(waypoint.position ?? 0, color),
          })
            .addTo(map)
            .bindTooltip(waypoint.label, { direction: 'top' });
        });

      route.polyline.forEach(coordinate => bounds.extend(coordinate));
    });

    map.fitBounds(bounds.pad(0.2));
    this.#map = map;
  }

  #depotIcon(): L.DivIcon {
    return L.divIcon({
      className: '',
      html: `<div style="background:#111827;color:#fff;width:28px;height:28px;border-radius:9999px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"><span class="material-icons" style="font-size:16px">home</span></div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }

  #jobIcon(position: number, color: string): L.DivIcon {
    return L.divIcon({
      className: '',
      html: `<div style="background:${color};color:#fff;width:26px;height:26px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">${position}</div>`,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
  }
}
